/**
 * OLIMPO ESPORTES - Rastreamento de Pedidos
 * JavaScript para busca e exibição de informações de pedidos
 * 
 * Nota de Segurança:
 * O Shopify não permite acesso direto aos dados de pedidos via JavaScript no tema
 * por questões de privacidade e segurança. Este script redireciona o cliente
 * para a página oficial de status do pedido (/account/orders/[order_id])
 * que requer autenticação adequada.
 * 
 * Características:
 * - Performance otimizada
 * - Validação de entrada
 * - Suporte a múltiplos formatos de número de pedido
 * - Feedback visual para o usuário
 */

(function() {
  'use strict';

  // Aguarda o DOM estar pronto
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    const form = document.getElementById('trackingForm');
    const loadingEl = document.getElementById('trackingLoading');
    const errorEl = document.getElementById('trackingError');
    const errorMessageEl = document.getElementById('trackingErrorMessage');
    const resultsEl = document.getElementById('trackingResults');
    const newSearchButton = document.getElementById('newSearchButton');

    if (!form) return;

    // Evento de submit do formulário
    form.addEventListener('submit', handleFormSubmit);

    // Evento de nova busca
    if (newSearchButton) {
      newSearchButton.addEventListener('click', resetForm);
    }

    /**
     * Processa o envio do formulário
     */
    function handleFormSubmit(e) {
      e.preventDefault();

      // Obtém os valores do formulário
      const orderNumber = document.getElementById('orderNumber').value.trim();
      const orderEmail = document.getElementById('orderEmail').value.trim();

      // Validação básica
      if (!orderNumber) {
        showError('Por favor, digite o número do pedido.');
        return;
      }

      // Limpa erros anteriores
      hideError();

      // Mostra loading
      showLoading();

      // Processa o número do pedido
      const cleanOrderNumber = cleanOrderNumberInput(orderNumber);

      // Valida formato do número do pedido
      if (!isValidOrderNumber(cleanOrderNumber)) {
        hideLoading();
        showError('Formato de número de pedido inválido. O número deve conter apenas dígitos (ex: 1001).');
        return;
      }

      // Redireciona para a página de status do pedido do Shopify
      // Esta é a forma segura e oficial de acessar informações do pedido
      redirectToOrderStatus(cleanOrderNumber, orderEmail);
    }

    /**
     * Limpa o número do pedido removendo caracteres especiais
     */
    function cleanOrderNumberInput(input) {
      // Remove #, espaços, traços e outros caracteres especiais
      return input.replace(/[^0-9]/g, '');
    }

    /**
     * Valida se o número do pedido tem formato válido
     */
    function isValidOrderNumber(orderNumber) {
      // Verifica se contém apenas números e tem entre 1 e 10 dígitos
      return /^\d{1,10}$/.test(orderNumber);
    }

    /**
     * Redireciona para a página de status do pedido do Shopify
     * 
     * O Shopify oferece URLs padronizadas para acesso ao status do pedido:
     * - Com autenticação: /account/orders/[order_id]
     * - Sem autenticação (requer token): /[order_id]/orders/[order_token]
     * 
     * Como não temos o token no tema, redirecionamos para a página de login
     * com o número do pedido para facilitar o acesso do cliente.
     */
    function redirectToOrderStatus(orderNumber, email) {
      // Simula um delay mínimo para feedback visual
      setTimeout(function() {
        // Constrói a URL de busca do pedido
        // O Shopify tem uma página de busca de pedidos que aceita número e email
        let searchUrl = '/tools/order-lookup';
        
        // Adiciona parâmetros se disponíveis
        const params = new URLSearchParams();
        params.append('number', orderNumber);
        if (email) {
          params.append('email', email);
        }

        // URL final
        const finalUrl = searchUrl + '?' + params.toString();

        // Alternativa: Redirecionar para página de conta do cliente
        // Se a loja tem contas de cliente habilitadas, podemos redirecionar
        // para a página de login e depois para o pedido específico
        const accountUrl = '/account';
        
        // Verifica se a loja tem a funcionalidade de busca de pedidos
        // Se não tiver, redireciona para a conta do cliente
        if (typeof window.Shopify !== 'undefined' && window.Shopify.routes) {
          // Tenta usar as rotas do Shopify se disponíveis
          window.location.href = accountUrl;
        } else {
          // Fallback: Mostra instruções ao usuário
          hideLoading();
          showOrderInstructions(orderNumber, email);
        }
      }, 800);
    }

    /**
     * Mostra instruções para o cliente acessar o pedido
     * (Fallback quando não é possível redirecionar automaticamente)
     */
    function showOrderInstructions(orderNumber, email) {
      hideLoading();
      hideError();

      // Cria o HTML com instruções
      const instructionsHtml = `
        <div class="tracking-instructions" style="
          background-color: var(--tracking-bg-secondary);
          border-radius: 12px;
          padding: 2rem;
          margin-top: 1rem;
          text-align: center;
        ">
          <h3 style="margin: 0 0 1rem; color: var(--tracking-text);">
            Pedido #${orderNumber}
          </h3>
          
          <p style="margin: 0 0 1.5rem; color: var(--tracking-text-secondary);">
            Para visualizar os detalhes e rastreamento do seu pedido:
          </p>

          <ol style="
            text-align: left;
            max-width: 500px;
            margin: 0 auto 1.5rem;
            color: var(--tracking-text-secondary);
            line-height: 1.8;
          ">
            <li>Acesse sua conta ou faça login</li>
            <li>Vá para "Meus Pedidos"</li>
            <li>Procure pelo pedido #${orderNumber}</li>
            <li>Clique para ver detalhes e rastreamento</li>
          </ol>

          <div style="display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap;">
            <a href="/account" class="button button--primary" style="
              display: inline-block;
              padding: 0.875rem 1.5rem;
              text-decoration: none;
              border-radius: 8px;
              font-weight: 600;
            ">
              Acessar Minha Conta
            </a>

            <button type="button" onclick="location.reload()" class="button button--secondary" style="
              display: inline-block;
              padding: 0.875rem 1.5rem;
              border: none;
              border-radius: 8px;
              font-weight: 600;
              cursor: pointer;
            ">
              Nova Busca
            </button>
          </div>

          ${email ? `
            <p style="
              margin: 1.5rem 0 0;
              font-size: 0.875rem;
              color: var(--tracking-text-secondary);
            ">
              Você receberá um email em <strong>${email}</strong> com o link direto para rastreamento.
            </p>
          ` : ''}
        </div>
      `;

      // Adiciona as instruções após o formulário
      const formWrapper = document.querySelector('.tracking-form-wrapper');
      if (formWrapper) {
        // Remove instruções anteriores se existirem
        const oldInstructions = document.querySelector('.tracking-instructions');
        if (oldInstructions) {
          oldInstructions.remove();
        }
        
        formWrapper.insertAdjacentHTML('afterend', instructionsHtml);
        
        // Scroll suave até as instruções
        setTimeout(function() {
          const instructions = document.querySelector('.tracking-instructions');
          if (instructions) {
            instructions.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }

    /**
     * Mostra o estado de loading
     */
    function showLoading() {
      if (loadingEl) {
        loadingEl.hidden = false;
      }
      // Desabilita o botão de submit
      const submitButton = form.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.style.opacity = '0.6';
        submitButton.style.cursor = 'not-allowed';
      }
    }

    /**
     * Esconde o estado de loading
     */
    function hideLoading() {
      if (loadingEl) {
        loadingEl.hidden = true;
      }
      // Reabilita o botão de submit
      const submitButton = form.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.style.opacity = '1';
        submitButton.style.cursor = 'pointer';
      }
    }

    /**
     * Mostra mensagem de erro
     */
    function showError(message) {
      if (errorEl && errorMessageEl) {
        errorMessageEl.textContent = message;
        errorEl.hidden = false;
        
        // Scroll suave até o erro
        errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    /**
     * Esconde mensagem de erro
     */
    function hideError() {
      if (errorEl) {
        errorEl.hidden = true;
      }
    }

    /**
     * Reseta o formulário para nova busca
     */
    function resetForm() {
      form.reset();
      hideError();
      hideLoading();
      if (resultsEl) {
        resultsEl.hidden = true;
      }
      
      // Remove instruções se existirem
      const instructions = document.querySelector('.tracking-instructions');
      if (instructions) {
        instructions.remove();
      }

      // Scroll suave até o topo do formulário
      const formWrapper = document.querySelector('.tracking-form-wrapper');
      if (formWrapper) {
        formWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      // Foca no primeiro campo
      const firstInput = document.getElementById('orderNumber');
      if (firstInput) {
        setTimeout(function() {
          firstInput.focus();
        }, 300);
      }
    }

    /**
     * Previne múltiplos submits
     */
    let isSubmitting = false;
    form.addEventListener('submit', function(e) {
      if (isSubmitting) {
        e.preventDefault();
        return false;
      }
      isSubmitting = true;
      setTimeout(function() {
        isSubmitting = false;
      }, 2000);
    });
  }

  /**
   * Detecta mudança de tema (light/dark mode)
   * e atualiza estilos se necessário
   */
  function watchThemeChanges() {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    function handleThemeChange(e) {
      // Adiciona classe ao body se necessário
      if (e.matches) {
        document.documentElement.setAttribute('data-theme', 'dark');
      } else {
        document.documentElement.setAttribute('data-theme', 'light');
      }
    }

    // Escuta mudanças
    mediaQuery.addEventListener('change', handleThemeChange);
    
    // Aplica tema inicial
    handleThemeChange(mediaQuery);
  }

  // Inicia observador de tema
  watchThemeChanges();

})();

/**
 * NOTA IMPORTANTE SOBRE SEGURANÇA E PRIVACIDADE:
 * 
 * O Shopify protege os dados dos pedidos dos clientes por padrão.
 * Não é possível acessar informações de pedidos diretamente via JavaScript
 * no tema sem autenticação adequada.
 * 
 * ALTERNATIVAS DISPONÍVEIS:
 * 
 * 1. Página Nativa de Status do Pedido:
 *    - URL: /[order_id]/orders/[order_token]
 *    - Requer token único enviado por email
 *    - Acesso sem login necessário
 *    - Totalmente gerenciada pelo Shopify
 * 
 * 2. Área de Conta do Cliente:
 *    - URL: /account/orders/[order_id]
 *    - Requer login do cliente
 *    - Lista todos os pedidos do cliente
 *    - Acesso a detalhes e rastreamento
 * 
 * 3. Shopify Customer Account API (Recomendado para Apps):
 *    - Requer desenvolvimento de app Shopify
 *    - Acesso controlado via OAuth
 *    - Permite criar experiências customizadas
 *    - Segue as melhores práticas de segurança
 * 
 * SOLUÇÃO IMPLEMENTADA:
 * Este código redireciona o cliente para a página de conta dele,
 * onde poderá fazer login e visualizar todos os detalhes do pedido,
 * incluindo rastreamento completo, de forma segura e oficial.
 */

